import { useCallback, useEffect, useRef, useState } from "react";
import support from "../../assets/svgs/chatSupport.svg";
import classes from "./chatbox.module.css";
import {
  getConversation,
  handoverToAdmin,
  initiateChat,
} from "../../services/chatbotCalls";
import { getCookie, setCookie } from "../../utils/functions";

// "Talk to Stan": the website support assistant. Stan (an LLM) answers from
// the Awasource FAQ; visitors can ask for a person, and the support team
// answers from the admin dashboard (Support → Live chat).

const BOT_NAME = "Stan";
const SESSION_COOKIE = "awa_stan_session";
const LEGACY_SESSION_COOKIE = "sessionId";
const SESSION_DAYS = 7;
const MAX_MESSAGE_LENGTH = 2000;
// How often the widget checks for replies from the support team.
const POLL_OPEN_MS = 4000;
const POLL_CLOSED_MS = 15000;

const SUGGESTIONS = [
  "How do I apply for jobs?",
  "How does talent verification work?",
  "I want to hire talent",
];

const readSessionCookie = () =>
  getCookie(SESSION_COOKIE) || getCookie(LEGACY_SESSION_COOKIE) || "";

const clearSessionCookie = () => {
  setCookie(SESSION_COOKIE, "", 0);
  setCookie(LEGACY_SESSION_COOKIE, "", 0);
};

// The first readable reason in an API error ({ errors: { key: "reason" } }).
const errorText = (error) => {
  if (error?.response?.status === 429) {
    return "You're sending messages too quickly. Please wait a moment and try again.";
  }
  const errors = error?.response?.data?.errors;
  if (typeof errors === "string") return errors;
  if (errors && typeof errors === "object") {
    const first = Object.values(errors).find((v) => typeof v === "string");
    if (first) return first;
  }
  return "Something went wrong. Please try again.";
};

const formatTime = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const BotAvatar = () => (
  <div
    className="w-7 h-7 rounded-full bg-gradient-to-br from-[#151BAF] to-[#03075C] flex items-center justify-center flex-shrink-0 text-white text-xs font-semibold"
    aria-hidden="true"
  >
    S
  </div>
);

const AgentAvatar = ({ name }) => (
  <div
    className="w-7 h-7 rounded-full bg-[#E8F7EE] border border-[#BFE8CF] flex items-center justify-center flex-shrink-0 text-[#13693A] text-xs font-semibold"
    aria-hidden="true"
  >
    {(name || "A").charAt(0).toUpperCase()}
  </div>
);

const TypingIndicator = () => (
  <div className="flex items-end gap-2 mb-3" aria-label={`${BOT_NAME} is typing`}>
    <BotAvatar />
    <div className="bg-[#f0f1ff] border border-[#e0e1ff] rounded-2xl rounded-bl-sm px-4 py-3">
      <div className="flex items-center gap-1">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="w-2 h-2 rounded-full bg-[#04097B] opacity-60 animate-bounce"
            style={{ animationDelay: `${delay}ms` }}
          ></span>
        ))}
      </div>
    </div>
  </div>
);

const ChatMessage = ({ message }) => {
  const isUser = message?.sender === "user";
  const isSupport = message?.sender === "support";
  const label = isUser
    ? null
    : isSupport
      ? message?.senderName || "Awasource support"
      : BOT_NAME;

  return (
    <div
      className={`flex items-end gap-2 mb-3 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      {!isUser && (isSupport ? <AgentAvatar name={message?.senderName} /> : <BotAvatar />)}
      <div className={`flex flex-col max-w-[78%] ${isUser ? "items-end" : "items-start"}`}>
        {label && (
          <span className="text-[10px] text-gray-400 mb-0.5 ml-1">{label}</span>
        )}
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed break-words whitespace-pre-wrap ${
            isUser
              ? "bg-gradient-to-br from-[#151BAF] to-[#03075C] text-white rounded-br-sm"
              : isSupport
                ? "bg-[#F3FBF6] border border-[#CDEBD8] text-gray-800 rounded-bl-sm"
                : "bg-[#f0f1ff] border border-[#e0e1ff] text-gray-800 rounded-bl-sm"
          } ${message?.failed ? "opacity-60" : ""}`}
        >
          {message?.text}
        </div>
        {(message?.timestamp || message?.failed) && (
          <span className="text-[10px] text-gray-400 mt-0.5 mx-1">
            {message?.failed ? "Not sent" : formatTime(message.timestamp)}
          </span>
        )}
      </div>
    </div>
  );
};

const StatusBanner = ({ status, agentName }) => {
  if (status === "waiting_for_support") {
    return (
      <div className="px-4 py-2 bg-[#FFF8E6] border-b border-[#F6E3B4] text-xs text-[#7A5600] flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-[#E5A800] animate-pulse"></span>
        Waiting for a member of our team to join…
      </div>
    );
  }
  if (status === "with_support") {
    return (
      <div className="px-4 py-2 bg-[#F3FBF6] border-b border-[#CDEBD8] text-xs text-[#13693A] flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
        You're chatting with {agentName || "our support team"}
      </div>
    );
  }
  return null;
};

const Chatbox = () => {
  const [chatOpen, setChatOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [status, setStatus] = useState("bot");
  const [agentName, setAgentName] = useState(null);
  const [hasContactEmail, setHasContactEmail] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [showHandoverForm, setShowHandoverForm] = useState(false);
  const [handoverDetails, setHandoverDetails] = useState({ name: "", email: "" });
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [notice, setNotice] = useState("");

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const chatOpenRef = useRef(chatOpen);
  const messageCountRef = useRef(0);
  const sendingRef = useRef(false);

  useEffect(() => {
    chatOpenRef.current = chatOpen;
  }, [chatOpen]);

  useEffect(() => {
    if (chatOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setHasUnread(false);
    }
  }, [messages, chatOpen, status, showHandoverForm]);

  useEffect(() => {
    if (chatOpen) textareaRef.current?.focus();
  }, [chatOpen]);

  const resetConversation = useCallback(() => {
    clearSessionCookie();
    setSessionId("");
    setMessages([]);
    setStatus("bot");
    setAgentName(null);
    setHasContactEmail(false);
    setShowHandoverForm(false);
    setNotice("");
    messageCountRef.current = 0;
  }, []);

  // Loads the conversation from the server (the source of truth). Returns
  // false when the session no longer exists.
  const loadConversation = useCallback(
    async (sid) => {
      if (!sid) return true;
      try {
        const res = await getConversation(sid);
        const conversation = res?.data?.data?.conversation;
        if (!conversation) return true;
        // A send in flight owns the message list until it finishes.
        if (sendingRef.current) return true;

        const fetched = Array.isArray(conversation.messages)
          ? conversation.messages
          : [];
        if (fetched.length > messageCountRef.current && messageCountRef.current > 0) {
          const latest = fetched[fetched.length - 1];
          if (!chatOpenRef.current && latest?.sender !== "user") setHasUnread(true);
        }
        messageCountRef.current = fetched.length;
        setMessages(fetched);
        setStatus(conversation.status || "bot");
        setAgentName(conversation.agentName || null);
        setHasContactEmail(Boolean(conversation.hasContactEmail));
        return true;
      } catch (error) {
        if (error?.response?.status === 404) return false;
        return true; // network blip: keep what we have, try again next poll
      }
    },
    [],
  );

  // Restore the visitor's conversation from the cookie.
  useEffect(() => {
    const existing = readSessionCookie();
    if (!existing) return;
    setSessionId(existing);
    setCookie(SESSION_COOKIE, existing, SESSION_DAYS);
    setIsInitializing(true);
    loadConversation(existing)
      .then((found) => {
        if (!found) resetConversation();
      })
      .finally(() => setIsInitializing(false));
  }, [loadConversation, resetConversation]);

  // Poll for replies: often while the chat is open, now and then while it is
  // closed but a person is (or will be) answering, and never in a hidden tab.
  useEffect(() => {
    if (!sessionId) return undefined;
    const humanThread = status === "waiting_for_support" || status === "with_support";
    if (!chatOpen && !humanThread) return undefined;

    const interval = chatOpen ? POLL_OPEN_MS : POLL_CLOSED_MS;
    const timer = setInterval(() => {
      if (document.visibilityState === "hidden") return;
      loadConversation(sessionId);
    }, interval);
    return () => clearInterval(timer);
  }, [sessionId, status, chatOpen, loadConversation]);

  const sendMessage = async (rawText) => {
    const trimmed = (rawText ?? input).trim();
    if (!trimmed || loading) return;
    if (trimmed.length > MAX_MESSAGE_LENGTH) {
      setNotice(`Messages can be at most ${MAX_MESSAGE_LENGTH} characters.`);
      return;
    }

    // Sending after a resolved conversation starts a fresh one.
    const startingFresh = status === "resolved";
    const optimistic = { sender: "user", text: trimmed, timestamp: new Date().toISOString() };
    setMessages((prev) => [...(startingFresh ? [] : prev), optimistic]);
    if (startingFresh) {
      setStatus("bot");
      setAgentName(null);
      setHasContactEmail(false);
    }
    setInput("");
    setNotice("");
    setLoading(true);
    sendingRef.current = true;
    if (textareaRef.current) textareaRef.current.style.height = "auto";

    try {
      const res = await initiateChat({
        message: trimmed,
        sessionId: startingFresh ? undefined : sessionId || undefined,
      });
      const data = res?.data?.data;
      if (data?.sessionId) {
        setCookie(SESSION_COOKIE, data.sessionId, SESSION_DAYS);
        setSessionId(data.sessionId);
        if (data.status) setStatus(data.status);
        // Show the reply right away, then sync with the server's copy.
        if (data.botResponse) {
          setMessages((prev) => [
            ...prev,
            { sender: "bot", text: data.botResponse, timestamp: data.timestamp },
          ]);
        }
        sendingRef.current = false;
        await loadConversation(data.sessionId);
      }
    } catch (error) {
      setMessages((prev) =>
        prev.map((m) => (m === optimistic ? { ...m, failed: true } : m)),
      );
      setInput(trimmed);
      setNotice(errorText(error));
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  };

  const requestHuman = async (e) => {
    e?.preventDefault();
    if (!sessionId || handoverLoading) return;
    const email = handoverDetails.email.trim();
    const addingContact = status !== "bot";
    if (addingContact && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNotice("Enter an email address so we can follow up.");
      return;
    }
    setHandoverLoading(true);
    setNotice("");
    try {
      const userInfo = {};
      if (handoverDetails.name.trim()) userInfo.name = handoverDetails.name.trim();
      if (email) userInfo.email = email;
      await handoverToAdmin({
        sessionId,
        reason: addingContact ? "Visitor left contact details" : "Visitor asked to talk to a person",
        userInfo,
      });
      setShowHandoverForm(false);
      if (!addingContact) setStatus("waiting_for_support");
      if (email) setHasContactEmail(true);
      await loadConversation(sessionId);
    } catch (error) {
      // Already with support (another tab): just show the current state.
      if (error?.response?.status === 400) {
        setShowHandoverForm(false);
        await loadConversation(sessionId);
      } else {
        setNotice(errorText(error));
      }
    } finally {
      setHandoverLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 112)}px`;
  };

  const handleOpen = () => {
    setChatOpen(true);
    setHasUnread(false);
  };

  const handleWindowKeyDown = (e) => {
    if (e.key === "Escape") setChatOpen(false);
  };

  const isEmpty = messages?.length === 0 && !isInitializing;
  const canAskForHuman = !!sessionId && status === "bot" && messages.length > 0;
  const canLeaveEmail =
    !!sessionId &&
    !hasContactEmail &&
    (status === "waiting_for_support" || status === "with_support");
  const isResolved = status === "resolved";

  return (
    <div className="flex flex-col items-end">
      {/* Chat Window */}
      {chatOpen && (
        <div
          className={`${classes.chatWindow} flex flex-col bg-white border border-gray-200 shadow-2xl rounded-2xl mb-3 overflow-hidden`}
          role="dialog"
          aria-label={`Chat with ${BOT_NAME}`}
          onKeyDown={handleWindowKeyDown}
        >
          {/* Header */}
          <div className={`${classes.chatHeader} px-4 py-3 flex items-center gap-3`}>
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
              <img src={support} alt="" className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-semibold text-sm">
                {status === "with_support" && agentName
                  ? `${agentName} · Awasource`
                  : `${BOT_NAME} · Awasource assistant`}
              </p>
              <div className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>
                <p className="text-white/70 text-xs">
                  {status === "with_support"
                    ? "Support team"
                    : status === "waiting_for_support"
                      ? "Connecting you to our team"
                      : "Usually replies instantly"}
                </p>
              </div>
            </div>
            {sessionId && status === "bot" && messages.length > 0 && (
              <button
                type="button"
                onClick={resetConversation}
                className="text-white/70 hover:text-white text-[11px] px-2 py-1 rounded-lg hover:bg-white/10"
                title="Start a new conversation"
              >
                New chat
              </button>
            )}
            <button
              type="button"
              onClick={() => setChatOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10"
              aria-label="Close chat"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          <StatusBanner status={status} agentName={agentName} />

          {/* Messages area */}
          <div
            className={`${classes.messagesArea} flex-1 overflow-y-auto px-4 py-4 bg-white`}
            aria-live="polite"
          >
            {isInitializing ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-xs text-gray-400">Loading conversation...</span>
              </div>
            ) : isEmpty ? (
              <div className="flex flex-col items-center justify-center h-full text-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#151BAF] to-[#03075C] flex items-center justify-center text-white text-xl font-semibold">
                  S
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">
                    Hi, I'm {BOT_NAME} 👋
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    Ask me anything about Awasource, or ask to talk to our team.
                  </p>
                </div>
                <div className="flex flex-col gap-2 w-full mt-1">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => sendMessage(suggestion)}
                      className="text-xs text-[#04097B] border border-[#e0e1ff] bg-[#f8f9ff] hover:bg-[#f0f1ff] rounded-xl px-3 py-2 text-left"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <ChatMessage key={message?._id || index} message={message} />
                ))}
                {loading && status === "bot" && <TypingIndicator />}
                {isResolved && (
                  <div className="text-center my-3">
                    <p className="text-xs text-gray-400 mb-2">This conversation has ended.</p>
                    <button
                      type="button"
                      onClick={resetConversation}
                      className="text-xs text-white bg-gradient-to-br from-[#151BAF] to-[#03075C] rounded-xl px-4 py-2"
                    >
                      Start a new chat
                    </button>
                  </div>
                )}
              </>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Talk to a human, or leave an email once a person is in the chat */}
          {canLeaveEmail && !showHandoverForm && (
            <div className="px-4 pt-2 bg-[#f8f9ff] border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowHandoverForm(true)}
                className="text-xs text-[#04097B] font-medium hover:underline"
              >
                Leave your email so we can follow up if you close this page
              </button>
            </div>
          )}
          {canAskForHuman && !showHandoverForm && (
            <div className="px-4 pt-2 bg-[#f8f9ff] border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowHandoverForm(true)}
                className="text-xs text-[#04097B] font-medium hover:underline"
              >
                Talk to a human
              </button>
            </div>
          )}
          {showHandoverForm && (
            <form
              onSubmit={requestHuman}
              className="px-4 py-3 bg-[#f8f9ff] border-t border-gray-100 flex flex-col gap-2"
            >
              <p className="text-xs text-gray-600">
                {canLeaveEmail
                  ? "We'll email you the reply if you close this page. The chat also stays in this browser."
                  : "Leave your details (optional) so our team can follow up if you leave."}
              </p>
              <input
                type="text"
                placeholder="Your name"
                maxLength={100}
                value={handoverDetails.name}
                onChange={(e) => setHandoverDetails((d) => ({ ...d, name: e.target.value }))}
                className="bg-white border border-gray-200 focus:border-[#04097B] focus:outline-none rounded-lg py-2 px-3 text-sm"
              />
              <input
                type="email"
                placeholder="Your email"
                maxLength={200}
                value={handoverDetails.email}
                onChange={(e) => setHandoverDetails((d) => ({ ...d, email: e.target.value }))}
                className="bg-white border border-gray-200 focus:border-[#04097B] focus:outline-none rounded-lg py-2 px-3 text-sm"
              />
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowHandoverForm(false)}
                  className="text-xs text-gray-500 px-3 py-2"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={handoverLoading}
                  className="text-xs text-white bg-gradient-to-br from-[#151BAF] to-[#03075C] rounded-lg px-4 py-2 disabled:opacity-50"
                >
                  {handoverLoading ? "Saving…" : canLeaveEmail ? "Save email" : "Connect me"}
                </button>
              </div>
            </form>
          )}

          {/* Input area */}
          {!isResolved && (
            <div className="px-4 py-3 bg-[#f8f9ff] border-t border-gray-100">
              {notice && (
                <p className="text-[11px] text-red-500 mb-2" role="alert">
                  {notice}
                </p>
              )}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
                className="flex flex-nowrap items-center gap-2 flex-row"
              >
                <textarea
                  ref={textareaRef}
                  rows="1"
                  maxLength={MAX_MESSAGE_LENGTH}
                  className="flex-1 min-w-0 resize-none bg-white border border-gray-200 focus:border-[#04097B] focus:outline-none rounded-xl py-2.5 px-3.5 text-sm text-gray-800 placeholder-gray-400 transition-colors max-h-28 overflow-y-auto"
                  placeholder={
                    status === "bot" ? `Message ${BOT_NAME}…` : "Message our team…"
                  }
                  aria-label="Type your message"
                  readOnly={loading}
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="submit"
                  disabled={loading || !input.trim()}
                  className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#151BAF] to-[#03075C] flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
                  aria-label="Send message"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </button>
              </form>
              <p className="text-[10px] text-gray-400 text-center mt-2">
                {BOT_NAME} is an AI assistant and can make mistakes · Shift+Enter for a new line
              </p>
            </div>
          )}
        </div>
      )}

      {/* Launcher */}
      <button
        type="button"
        onClick={chatOpen ? () => setChatOpen(false) : handleOpen}
        className={`relative h-14 rounded-full bg-gradient-to-br from-[#151BAF] to-[#03075C] flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 cursor-pointer ${
          chatOpen ? "w-14" : "pl-3 pr-5"
        }`}
        aria-label={chatOpen ? "Close chat" : `Talk to ${BOT_NAME}`}
      >
        {chatOpen ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <>
            <img src={support} alt="" className="w-8 h-8" />
            <span className="text-white text-sm font-semibold whitespace-nowrap">
              Talk to {BOT_NAME}
            </span>
          </>
        )}
        {hasUnread && !chatOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white">
            <span className="sr-only">New message</span>
          </span>
        )}
      </button>
    </div>
  );
};

export default Chatbox;
